import { css } from '@emotion/react';

import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import FormItem from 'components/form/FormItem';

import useUpdateEffect from 'hooks/useUpdateEffect';

import BoxRow from 'pages/Shipments/ActionForms/CreateShipment/Components/BoxRow';

import { Breakpoints } from 'utils/const.styles';
import { roundNumber } from 'utils/utils';

import { faPlus } from '@fortawesome/pro-light-svg-icons';

import * as S from '../OTOLink.styled';

const defaultColSpan = {
	md: 6
};

const AddBox = ({ mode }) => {
	const viewMode = mode === 'view';
	const addMode = mode !== 'edit' && mode !== 'view';
	const editMode = mode === 'edit';

	const {
		control,
		setValue,
		getValues,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = useFormContext();

	const isLoading = isSubmitting || isFormLoading;

	const boxFields = useWatch({
		control,
		name: 'boxes'
	});

	const boxDetailsAccordionRef = useRef(null);

	const { defaultBox: defaultBoxState } = useSelector((state) => state.user);
	const defaultBox = useMemo(
		() => ({
			ID: defaultBoxState?.id,
			width: defaultBoxState?.width || 10,
			height: defaultBoxState?.height || 10,
			length: defaultBoxState?.length || 10,
			name: defaultBoxState?.boxName,
			weight: 1
		}),
		[defaultBoxState]
	);

	const { fields, remove, append } = useFieldArray({
		control,
		name: 'boxes'
	});

	const handleAddBox = useCallback(() => {
		append(defaultBox);
	}, []);

	useUpdateEffect(() => {
		if (addMode || editMode) {
			const currentWeight = getValues('weight');
			const currentPackageCount = getValues('packageCount');

			if (boxFields?.length > 0) {
				const newWeight = roundNumber(boxFields.reduce((acc, curr) => acc + curr.weight, 0));
				const newPackageCount = boxFields.length;
				if (currentWeight !== newWeight) {
					setValue('weight', newWeight, {
						shouldValidate: true,
						shouldDirty: true
					});
				}
				if (currentPackageCount !== newPackageCount) {
					setValue('packageCount', newPackageCount, {
						shouldValidate: true,
						shouldDirty: true
					});
				}
			}
		}
	}, [boxFields]);

	return (
		<S.FullWidthAccordion
			ref={boxDetailsAccordionRef}
			header="boxDetails"
			headerTooltip="boxDetailsAccordionTooltip"
			defaultVisible>
			{fields?.map((field, index) => (
				<BoxRow
					key={`${field.id}${index}`}
					index={index}
					remove={remove}
					fieldName="boxes"
					disabled={viewMode || editMode}
					required
				/>
			))}

			<FormItem colSpan={defaultColSpan} label="packageCount" isLoading={isLoading}>
				<IntegerInput disabled={viewMode || boxFields?.length > 0} name="packageCount" unit="pcs" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="weight" isLoading={isLoading}>
				<IntegerInput
					disabled={viewMode || boxFields?.length > 0}
					name="weight"
					unit="kg"
					decimalLimit={2}
					allowFloat
				/>
			</FormItem>

			{!viewMode && !editMode && (
				<FormItem>
					<Button
						label="addBox"
						icon={faPlus}
						variant="outline"
						onClick={handleAddBox}
						css={css`
							@media ${Breakpoints.mobile} {
								width: 100%;
							}
						`}
					/>
				</FormItem>
			)}
		</S.FullWidthAccordion>
	);
};

export default AddBox;
